import React, { useState, useMemo } from 'react';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Subtitle1Bold, CaptionLight } from '../../lib/font';
import { useGroupSettlement } from '@/features/groupSettlement/hooks';
import { GroupSettlementByCoach, GroupSettlementSession } from '@/features/groupSettlement/types';
import { useMemberTicketPayments } from '@/features/memberTicketPayment/hooks';
import dayjs from 'dayjs';

const CLASS_TYPE_LABEL: Record<string, string> = {
    GROUP: '그룹', PRIVATE_1ON2: '1:2', PRIVATE_1ON3: '1:3',
};

const navButtonStyle: React.CSSProperties = {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid #E5E7EB',
    backgroundColor: 'white',
    color: '#374151',
    fontSize: 18,
    lineHeight: 1,
    cursor: 'pointer',
};

const GroupSettlementPage = ({
    initialYear,
    initialMonth,
}: {
    createAlert?: any;
    initialYear?: number;
    initialMonth?: number;
}) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const centerId = authObject?.centerId;
    const [targetMonth, setTargetMonth] = useState(() => {
        const base = dayjs()
            .year(initialYear ?? dayjs().year())
            .month((initialMonth ?? dayjs().month() + 1) - 1)
            .startOf('month');
        return base;
    });
    const year = targetMonth.year();
    const month = targetMonth.month() + 1;

    const { data: settlementData, isLoading, isError } = useGroupSettlement({ centerId, year, month });

    // 판매 매출: 해당 월의 MemberTicketPayment 조회
    const monthStart = dayjs(`${year}-${String(month).padStart(2, '0')}-01`).format('YYYY-MM-DD');
    const monthEnd = dayjs(monthStart).endOf('month').format('YYYY-MM-DD');
    const { data: paymentData, isLoading: isPaymentLoading } = useMemberTicketPayments({
        centerId,
        params: { startDate: monthStart, endDate: monthEnd },
        enabled: !!centerId,
    });

    const salesSummary = useMemo(() => {
        const payments = paymentData?.results ?? [];
        let totalSales = 0;
        let totalRefund = 0;
        let salesCount = 0;
        let refundCount = 0;
        for (const p of payments) {
            totalSales += p.paymentAmount ?? 0;
            totalRefund += p.cancelAmount ?? 0;
            salesCount += 1;
            if (p.saleStatus === 'FULL_REFUND' || p.saleStatus === 'PARTIAL_REFUND') refundCount += 1;
        }
        return { totalSales, totalRefund, netRevenue: totalSales - totalRefund, salesCount, refundCount };
    }, [paymentData]);

    const [activeTab, setActiveTab] = useState<'coach' | 'session'>('coach');

    const summary = settlementData?.summary;
    const byCoach = settlementData?.byCoach ?? [];
    const sessions = settlementData?.sessions ?? [];

    return (
        <Col style={{ width: '100%', height: '100%', backgroundColor: '#F8FAFC', marginTop: 64, overflow: 'auto' }}>
            <Col style={{ width: '100%', maxWidth: 1200, margin: '0 auto', padding: '40px 32px 80px' }}>
                <Row style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <Col>
                        <Subtitle1Bold>그룹 수업 강사 정산</Subtitle1Bold>
                        <CaptionLight className="!text-[13px]" color={colors.LAVEL_1} style={{ marginTop: 4 }}>
                            그룹 · 프라이빗(1:2, 1:3) 수업의 판매 매출과 수업료 정산을 확인합니다
                        </CaptionLight>
                    </Col>
                    <Row style={{ alignItems: 'center', gap: 10 }}>
                        <button
                            type="button"
                            style={navButtonStyle}
                            onClick={() => setTargetMonth((prev) => prev.subtract(1, 'month'))}
                            aria-label="이전 달"
                        >
                            ‹
                        </button>
                        <span style={{ fontSize: 18, fontWeight: 700, color: '#111827', minWidth: 120, textAlign: 'center' }}>
                            {year}년 {month}월
                        </span>
                        <button
                            type="button"
                            style={navButtonStyle}
                            onClick={() => setTargetMonth((prev) => prev.add(1, 'month'))}
                            aria-label="다음 달"
                        >
                            ›
                        </button>
                    </Row>
                </Row>

                {/* ── 판매 매출 섹션 ── */}
                <Row style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Subtitle1Bold>{year}년 {month}월 판매 매출</Subtitle1Bold>
                    <CaptionLight className="!text-[13px]" color={colors.LAVEL_1}>수강권 판매 시점 기준 매출</CaptionLight>
                </Row>

                {isPaymentLoading ? (
                    <Row style={{ gap: 12, marginBottom: 32 }}>
                        {[1, 2, 3].map((i) => (
                            <Col key={i} style={{ flex: 1, backgroundColor: '#F3F4F6', borderRadius: 12, padding: '18px 20px', height: 80, animation: 'pulse 1.5s infinite' }} />
                        ))}
                    </Row>
                ) : (
                    <Row style={{ gap: 12, marginBottom: 32 }}>
                        <SummaryCard label="판매액" value={`${salesSummary.totalSales.toLocaleString()}원`} color="#2563EB" bg="#EFF6FF" />
                        <SummaryCard label="환불액" value={`${salesSummary.totalRefund.toLocaleString()}원`} color="#DC2626" bg="#FEF2F2" />
                        <SummaryCard label="순매출" value={`${salesSummary.netRevenue.toLocaleString()}원`} color="#065F46" bg="#ECFDF5" />
                    </Row>
                )}

                <div style={{ borderTop: '1px solid #E5E7EB', marginBottom: 28 }} />

                {/* ── 수업료 정산 섹션 ── */}
                <Row style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <Subtitle1Bold>{year}년 {month}월 수업료 정산</Subtitle1Bold>
                    <CaptionLight className="!text-[13px]" color={colors.LAVEL_1}>그룹 · 프라이빗(1:2, 1:3) 수업 기준 정산</CaptionLight>
                </Row>

                {/* 요약 카드 */}
                {isLoading ? (
                    <Row style={{ gap: 12, marginBottom: 28 }}>
                        {[1, 2, 3, 4].map((i) => (
                            <Col key={i} style={{ flex: 1, backgroundColor: '#F3F4F6', borderRadius: 12, padding: '18px 20px', height: 80, animation: 'pulse 1.5s infinite' }} />
                        ))}
                    </Row>
                ) : isError ? (
                    <Col style={{ padding: '20px 0', marginBottom: 20 }}>
                        <span style={{ fontSize: 14, color: '#DC2626' }}>정산 데이터를 불러오지 못했습니다.</span>
                    </Col>
                ) : (
                    <Row style={{ gap: 12, marginBottom: 28 }}>
                        <SummaryCard label="총 수업료" value={`${(summary?.totalLessonFee ?? summary?.totalRevenue ?? 0).toLocaleString()}원`} color="#2563EB" bg="#EFF6FF" />
                        <SummaryCard label="강사 정산액" value={`${(summary?.settlementAmount ?? 0).toLocaleString()}원`} color="#7C3AED" bg="#F5F3FF" />
                        <SummaryCard label="수업 횟수" value={`${summary?.sessionCount ?? 0}회`} color="#065F46" bg="#ECFDF5" />
                        <SummaryCard label="총 수강 인원" value={`${summary?.memberCount ?? 0}명`} color="#92400E" bg="#FEF3C7" />
                    </Row>
                )}

                {/* 탭: 강사별 / 수업별 */}
                <Row style={{ gap: 0, borderBottom: '2px solid #E5E7EB', marginBottom: 20 }}>
                    {(['coach', 'session'] as const).map((tab) => (
                        <button key={tab} onClick={() => setActiveTab(tab)}
                            style={{
                                padding: '10px 20px', fontSize: 14, fontWeight: activeTab === tab ? 700 : 400,
                                color: activeTab === tab ? '#111827' : '#6B7280',
                                background: 'none', border: 'none', cursor: 'pointer',
                                borderBottom: activeTab === tab ? '2px solid #111827' : '2px solid transparent',
                                marginBottom: -2,
                            }}>
                            {tab === 'coach' ? '강사별 정산' : '수업별 내역'}
                        </button>
                    ))}
                </Row>

                {/* 강사별 매출 */}
                {activeTab === 'coach' && (
                    <Col style={{ gap: 0 }}>
                        <div className="scrollbar scrollbar-h-[10px] scrollbar-thumb-[#6B7280] scrollbar-track-[#EEF0F2]" style={{ border: '1px solid #E5E7EB', borderRadius: 8, overflowX: 'auto' }}>
                            <table style={{ width: '100%', minWidth: 1000, borderCollapse: 'collapse', fontSize: 14 }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#F9FAFB' }}>
                                        <th style={thStyle}>강사</th>
                                        <th style={thStyle}>수업 수</th>
                                        <th style={thStyle}>출석</th>
                                        <th style={thStyle}>노쇼</th>
                                        <th style={thStyle}>패널티</th>
                                        <th style={thStyle}>수업료</th>
                                        <th style={thStyle}>정산액</th>
                                        <th style={{ ...thStyle, width: 80 }}>정산 설정</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading && (
                                        <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#6B7280', padding: 24 }}>불러오는 중...</td></tr>
                                    )}
                                    {!isLoading && byCoach.length === 0 && (
                                        <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#6B7280', padding: 24 }}>이번 달 수업 매출이 없습니다</td></tr>
                                    )}
                                    {byCoach.map((c: GroupSettlementByCoach) => {
                                        const hasConfig = (c as any).hasCommissionConfig;
                                        const ctype = (c as any).commissionType;
                                        return (
                                            <tr key={c.coachId} style={{ borderTop: '1px solid #F3F4F6' }}>
                                                <td style={{ ...tdStyle, fontWeight: 600 }}>{c.coachName || c.coachId}</td>
                                                <td style={tdStyle}>{c.sessions ?? 0}회</td>
                                                <td style={tdStyle}>{c.completedSessions != null ? c.completedSessions : '-'}</td>
                                                <td style={tdStyle}>
                                                    {(c.noshowSessions ?? 0) > 0
                                                        ? <span style={{ color: '#DC2626', fontWeight: 600 }}>{c.noshowSessions}</span>
                                                        : '-'}
                                                </td>
                                                <td style={tdStyle}>
                                                    {(c.penaltySessions ?? 0) > 0
                                                        ? <span style={{ color: '#92400E', fontWeight: 600 }}>{c.penaltySessions}</span>
                                                        : '-'}
                                                </td>
                                                <td style={{ ...tdStyle, fontWeight: 600, color: '#2563EB' }}>{(c.lessonFeeTotal ?? c.revenue).toLocaleString()}원</td>
                                                <td style={{ ...tdStyle, fontWeight: 600, color: '#7C3AED' }}>
                                                    {(c.settlement ?? c.settlementAmount).toLocaleString()}원
                                                    {!hasConfig && (
                                                        <span style={{ marginLeft: 6, fontSize: 13, color: '#DC2626' }}>(미설정)</span>
                                                    )}
                                                </td>
                                                <td style={tdStyle}>
                                                    <span style={{ fontSize: 13, color: '#6B7280' }}>
                                                        {hasConfig ? (ctype === 'percentage' ? '%' : '건당') : '-'}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {/* 합계 */}
                                    {byCoach.length > 0 && (
                                        <tr style={{ borderTop: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                                            <td style={{ ...tdStyle, fontWeight: 700 }}>합계</td>
                                            <td style={{ ...tdStyle, fontWeight: 700 }}>{byCoach.reduce((s, c) => s + (c.sessions ?? 0), 0)}회</td>
                                            <td style={{ ...tdStyle, fontWeight: 700 }}>{byCoach.reduce((s, c) => s + (c.completedSessions ?? 0), 0)}</td>
                                            <td style={{ ...tdStyle, fontWeight: 700, color: '#DC2626' }}>{byCoach.reduce((s, c) => s + (c.noshowSessions ?? 0), 0)}</td>
                                            <td style={{ ...tdStyle, fontWeight: 700, color: '#92400E' }}>{byCoach.reduce((s, c) => s + (c.penaltySessions ?? 0), 0)}</td>
                                            <td style={{ ...tdStyle, fontWeight: 700, color: '#2563EB' }}>{(summary?.totalLessonFee ?? summary?.totalRevenue ?? 0).toLocaleString()}원</td>
                                            <td style={{ ...tdStyle, fontWeight: 700, color: '#7C3AED' }}>{(summary?.settlementAmount ?? 0).toLocaleString()}원</td>
                                            <td style={tdStyle} />
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Col>
                )}

                {/* 수업별 내역 */}
                {activeTab === 'session' && (
                    <div className="scrollbar scrollbar-h-[10px] scrollbar-thumb-[#6B7280] scrollbar-track-[#EEF0F2]" style={{ border: '1px solid #E5E7EB', borderRadius: 8, overflowX: 'auto' }}>
                        <table style={{ width: '100%', minWidth: 1000, borderCollapse: 'collapse', fontSize: 14 }}>
                            <thead>
                                <tr style={{ backgroundColor: '#F9FAFB' }}>
                                    <th style={thStyle}>날짜</th>
                                    <th style={thStyle}>수업명</th>
                                    <th style={thStyle}>유형</th>
                                    <th style={thStyle}>강사</th>
                                    <th style={thStyle}>출석</th>
                                    <th style={thStyle}>노쇼</th>
                                    <th style={thStyle}>패널티</th>
                                    <th style={thStyle}>수업료</th>
                                    <th style={thStyle}>정산액</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading && (
                                    <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: '#6B7280', padding: 24 }}>불러오는 중...</td></tr>
                                )}
                                {!isLoading && sessions.length === 0 && (
                                    <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: '#6B7280', padding: 24 }}>이번 달 수업 내역이 없습니다</td></tr>
                                )}
                                {sessions.map((s: GroupSettlementSession) => (
                                    <tr key={s.sessionId} style={{ borderTop: '1px solid #F3F4F6' }}>
                                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                            {dayjs(s.date).format('M/D (ddd)')}<br />
                                            <span style={{ fontSize: 13, color: '#6B7280' }}>
                                                {dayjs(s.startTime).format('HH:mm')}~{dayjs(s.endTime).format('HH:mm')}
                                            </span>
                                        </td>
                                        <td style={{ ...tdStyle, fontWeight: 600 }}>{s.title}</td>
                                        <td style={tdStyle}>
                                            <span style={{
                                                fontSize: 13, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                                                backgroundColor: s.classType === 'GROUP' ? '#ECFDF5' : '#EFF6FF',
                                                color: s.classType === 'GROUP' ? '#065F46' : '#1E40AF',
                                            }}>
                                                {CLASS_TYPE_LABEL[s.classType] || s.classType}
                                            </span>
                                        </td>
                                        <td style={tdStyle}>
                                            {s.coachName || '-'}
                                            {s.originalCoachId && s.originalCoachId !== s.coachId && (
                                                <span style={{
                                                    marginLeft: 6, fontSize: 13, fontWeight: 600, padding: '1px 6px',
                                                    borderRadius: 4, backgroundColor: '#FEF3C7', color: '#92400E',
                                                }}>대강</span>
                                            )}
                                        </td>
                                        <td style={tdStyle}>{s.attendanceCount}/{s.maxCapacity}명</td>
                                        <td style={tdStyle}>
                                            {(s.noshowCount ?? 0) > 0
                                                ? <span style={{ color: '#DC2626', fontWeight: 600 }}>{s.noshowCount}</span>
                                                : '-'}
                                        </td>
                                        <td style={tdStyle}>
                                            {(s.penaltyCount ?? 0) > 0
                                                ? <span style={{ color: '#92400E', fontWeight: 600 }}>{s.penaltyCount}</span>
                                                : '-'}
                                        </td>
                                        <td style={{ ...tdStyle, fontWeight: 600, color: '#2563EB' }}>{s.revenue.toLocaleString()}원</td>
                                        <td style={{ ...tdStyle, color: '#7C3AED' }}>{s.settlementAmount.toLocaleString()}원</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* 강사별 정산 설정 안내 */}
                <Col style={{ marginTop: 40, padding: 16, backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8 }}>
                    <span style={{ fontSize: 14, color: '#1E40AF', fontWeight: 600, marginBottom: 4 }}>
                        강사별 정산 설정 안내
                    </span>
                    <span style={{ fontSize: 13, color: '#1E40AF', lineHeight: 1.5 }}>
                        그룹수업 정산은 PT와 동일하게 <b>[급여 계산]</b> 메뉴의 강사별 수수료 설정을 따릅니다.
                        설정이 없는 강사는 정산액이 0원으로 표시되며, "(미설정)" 표시가 함께 나옵니다.
                    </span>
                </Col>
            </Col>
        </Col>
    );
};

const SummaryCard = ({ label, value, color, bg }: { label: string; value: string; color: string; bg: string }) => (
    <Col style={{ flex: 1, backgroundColor: bg, borderRadius: 12, padding: '18px 20px', gap: 6 }}>
        <CaptionLight className="!text-[13px]" style={{ color }}>{label}</CaptionLight>
        <span style={{ fontSize: 22, fontWeight: 700, color }}>{value}</span>
    </Col>
);

const thStyle: React.CSSProperties = { padding: '10px 16px', textAlign: 'left', fontWeight: 500, color: '#6B7280', fontSize: 13 };
const tdStyle: React.CSSProperties = { padding: '10px 16px', color: '#374151', fontSize: 14 };

export default GroupSettlementPage;
